import { z } from "zod";
import { CategoryKind, MoneyBucket } from "../domain.js";
import { Direction, Id, IsoDate, IsoMonth, Pence } from "../primitives.js";

/**
 * The query DSL — docs/08 §8.2.
 *
 * One JSON shape describes every question the system can answer, every chart
 * it can draw, and every rule proposal it can make. It exists as a product
 * feature with its own endpoint and its own tests; the model is one client of
 * it, and it is built first so that a milestone has value with no model call
 * at all.
 *
 * **There is no `householdId` field, and there never will be.** The compiler
 * injects it from the session, so no model output and no API caller can widen
 * the scope. Every object here is strict, so asking for one is a validation
 * error rather than a silently ignored key — see `Query`.
 */

export const Measure = z.enum(["sum", "count", "average", "min", "max", "median"]);

export const GroupBy = z.enum(["month", "week", "category", "account", "person", "merchant"]);

export const SortOrder = z.enum(["value_desc", "value_asc", "key_asc", "key_desc"]);

/**
 * "Is that a lot?" is the actual question behind almost every finance query,
 * and a comparison the compiler computes is a comparison the model cannot get
 * wrong.
 */
export const CompareTo = z.enum(["previous_period", "same_period_last_year", "budget"]);

/**
 * Relative periods exist so a caller never has to do date arithmetic to ask
 * an ordinary question — and so the model in particular never has to, since
 * "the last six months" is exactly the kind of thing it would get wrong at a
 * year boundary.
 */
export const RelativePeriod = z.enum([
  "this-month",
  "last-month",
  "last-3-months",
  "last-6-months",
  "last-12-months",
  "this-year",
  "last-year",
  "all-time",
]);

/** `YYYY-MM` inclusive at both ends. */
export const AbsolutePeriod = z.strictObject({ from: IsoMonth, to: IsoMonth });

export const Period = z.union([RelativePeriod, AbsolutePeriod]);

/**
 * The subset of `RelativePeriod` offered as an explicit range on a month
 * picker — the picker's own list of real months already covers "this month"
 * one at a time, so what a range adds is the four spans nobody wants to pick
 * a month at a time for.
 */
export const RangePeriod = z.enum(["last-3-months", "last-6-months", "last-12-months", "all-time"]);

/**
 * The list lengths are not arbitrary: D1 allows **100 bound parameters per
 * query**. Twenty accounts, twenty categories and ten people is 50 parameters,
 * which leaves room for the period, the filters and the limit — and is far
 * past any household's real vocabulary.
 */
export const QueryFilters = z.strictObject({
  /** Account **keys**, not ids — the DSL reads better and keys are stable. */
  accounts: z.array(z.string().min(1).max(64)).max(20).optional(),
  /** Category **names**, as they read in the app. */
  categories: z.array(z.string().min(1).max(120)).max(20).optional(),
  people: z.array(z.string().min(1).max(64)).max(10).optional(),
  /**
   * Which of the three money figures to ask about, decided by the **kind** of
   * the category a row is filed under.
   *
   * Not the same as `direction`, and the difference is the whole reason this
   * exists. `direction` is the sign on the row: `out` means the amount is
   * negative. `bucket: "out"` means the row *is* spending — so a refund under
   * Groceries is in it, carrying a positive amount that comes back off the
   * total, exactly as it does on the dashboard and the budgets screen.
   *
   * Anything asking "what did we spend" wants `bucket`, not `direction`.
   * Without it the DSL could not be compared against a budget line without the
   * two quietly disagreeing about refunds.
   */
  bucket: MoneyBucket.optional(),
  direction: Direction.optional(),
  /** Free text. Bound as a LIKE parameter; nothing here reaches SQL as text. */
  descriptionContains: z.string().min(1).max(200).optional(),
  /**
   * Magnitudes, not signed amounts — `{ direction: "out", minPence: 1000 }`
   * means "spends of £10 or more", which is what anyone writing it intends.
   * The sign lives on `direction`.
   */
  minPence: Pence.min(0, "An amount filter is a magnitude, so it cannot be negative").optional(),
  maxPence: Pence.min(0, "An amount filter is a magnitude, so it cannot be negative").optional(),
  /** A pending row is the bank's guess at an amount that usually changes. */
  includePending: z.boolean().default(false),
  /** Rows somebody hid by hand. */
  includeExcluded: z.boolean().default(false),
  /**
   * Rows filed under a category of kind `transfer`, or overridden to it — one
   * payment written down twice, so counting them invents income and spending.
   *
   * Its own flag rather than sharing `includeExcluded`, which is what it did:
   * "show me the transfers" and "show me the rows I hid" are different requests,
   * and one switch for both meant the first silently did the second.
   */
  includeTransfers: z.boolean().default(false),
});

export const Query = z.strictObject({
  measure: Measure.default("sum"),
  filters: QueryFilters.default({
    includePending: false,
    includeExcluded: false,
    includeTransfers: false,
  }),
  period: Period.default("last-12-months"),
  /** Absent means one row for the whole period. */
  groupBy: GroupBy.optional(),
  sort: SortOrder.default("key_asc"),
  /** Capped at 100 by the schema, so the cap cannot be forgotten server-side. */
  limit: z.number().int().min(1).max(100).default(100),
  compareTo: CompareTo.optional(),
});

/**
 * A result row. `value` is integer pence for every measure except `count`,
 * which is a plain integer — `unit` on the result says which, so the client
 * never has to infer it from the measure.
 */
export const QueryRow = z.object({
  /** The raw group key: `2026-07`, an account key, a category name. */
  key: z.string(),
  /** The same key as a person would read it. Equal to `key` when there is nothing to add. */
  label: z.string(),
  value: z.number(),
  /** How many transactions are behind the figure. Every number is checkable. */
  count: z.number().int(),
  /**
   * The comparable figure from `compareTo`, on the same row, so that reading
   * "over" or "under" off it is subtraction and nothing else.
   *
   * Null when this key has no counterpart — a category with no budget, a month
   * with no equivalent. Null is not zero, and the difference matters: a
   * category nobody budgeted is not a category budgeted at nothing.
   *
   * **It carries the same sign convention as `value`.** A £500 monthly budget
   * against spending appears as −50000, because spending is negative
   * everywhere else in the system and a comparison whose two halves need
   * different signs is a comparison waiting to be got wrong.
   */
  baseline: z.number().nullable(),
  baselineCount: z.number().int().nullable(),
});

export const ResolvedPeriod = z.object({ from: IsoMonth.nullable(), to: IsoMonth.nullable() });

export const QueryTotal = z.object({
  value: z.number(),
  count: z.number().int(),
  baseline: z.number().nullable(),
  baselineCount: z.number().int().nullable(),
});

export const QueryResult = z.object({
  /** The query as parsed, defaults filled in. What actually ran. */
  query: Query,
  /**
   * The query in plain English, rendered from the object rather than written
   * by anything that could misdescribe it (docs/08 §8.4).
   */
  description: z.string(),
  unit: z.enum(["pence", "count"]),
  /** The resolved period, so a relative key is never ambiguous after the fact. */
  period: ResolvedPeriod,
  rows: z.array(QueryRow),
  /** The whole filtered set, not the rows above — a page that sums itself lies. */
  total: QueryTotal,
  comparison: z
    .object({
      kind: CompareTo,
      description: z.string(),
      /** Null for a budget comparison, which spans the same period as the query. */
      period: ResolvedPeriod.nullable(),
      /** The baseline as its own series, so a chart can draw both. */
      rows: z.array(QueryRow),
      total: z.object({ value: z.number(), count: z.number().int() }),
    })
    .nullable(),
  /** True when `limit` cut the result short, so the UI can say so. */
  truncated: z.boolean(),
});

/**
 * Budgets, dated by month.
 *
 * A budget is a row that starts in a month and optionally ends in one, so
 * "did we go over in March?" is answered against March's budget rather than
 * today's. That is the difference between a budget tool and a spreadsheet.
 */
export const BudgetPeriod = z.enum(["monthly", "annual"]);

export const BudgetLine = z.object({
  /** Null when the category has spending this month but no budget set. */
  id: Id.nullable(),
  categoryId: Id,
  categoryName: z.string(),
  /**
   * The kind of the category, because a line does not always measure spending.
   * A budget on a `saving` category is a target for money set aside, and
   * "£3,076.28 of £2,625 spent" is the wrong sentence about a savings pot
   * however right the arithmetic is.
   */
  kind: CategoryKind,
  colour: z.string().nullable(),
  period: BudgetPeriod.nullable(),
  /** As entered: the annual figure for an annual budget. Null when unbudgeted. */
  amountPence: Pence.nullable(),
  /** What that works out to for this one month. The only figure worth comparing. */
  monthlyAllowancePence: Pence.nullable(),
  /**
   * Signed, and **not** a magnitude: positive is money that left or was set
   * aside, negative is money that came back. A category the month's refunds
   * left in credit, or a savings pot drawn down, has a negative figure here
   * rather than a positive one that reads as its opposite.
   */
  spentPence: Pence,
  /** Allowance minus spend. Negative means over. Null when unbudgeted. */
  remainingPence: Pence.nullable(),
  txnCount: z.number().int(),
  startsMonth: IsoMonth.nullable(),
  endsMonth: IsoMonth.nullable(),
  /**
   * Which accounts this figure is measured against. Empty means every
   * account — the household's ordinary case, and the only one there was
   * before a budget could be restricted at all — so an old budget needs no
   * migration to keep meaning what it always meant.
   *
   * Null when the category has no budget this month: an unbudgeted line has
   * no restriction to report, because it has nothing that could be restricted.
   */
  accountIds: z.array(Id).nullable(),
  /**
   * There is deliberately no `rollover` here.
   *
   * The column exists from migration 0002 and nothing computes it: carrying an
   * underspend into the next month is not implemented, so the flag was false
   * on every row it was ever returned on. A field that is always false is a
   * field every client has to handle and none can learn anything from, and
   * "surfaced with the gap documented" turned out to mean "documented in a
   * comment nobody reading the JSON would see".
   *
   * The column stays. Adding the field back is additive and costs a schema
   * line on the day rollover actually does something.
   */
});

export const BudgetSummary = z.object({
  month: IsoMonth,
  /** Every category with a budget this month, or with spending in it, or both. */
  lines: z.array(BudgetLine),
  totalAllowancePence: Pence,
  /**
   * What came in that month: booked rows, nothing excluded, and decided by the
   * kind of the category a row is filed under rather than by the sign of its
   * amount — so a refund is negative spending here, as it is on the dashboard.
   * A budget only means anything against the money there was to budget.
   */
  totalIncomePence: Pence,
  /**
   * All spending in the month, so that
   * `totalSpent = (sum of budgeted spending lines) + unbudgeted`.
   *
   * Split by `inBucket`, so this is the **same figure** the dashboard shows as
   * money out for the same month. The two screens disagreed for as long as this
   * was the sum of every negative row: that counted money moved into a savings
   * pot as spending and ignored refunds entirely.
   */
  totalSpentPence: Pence,
  /**
   * Money set aside that month, net of anything drawn back out — the
   * dashboard's "Saved" for the same month. Deliberately not folded into
   * `totalSpentPence`: a budgeted savings pot is a real allowance being used,
   * but using it is not spending.
   */
  totalSavedPence: Pence,
  /**
   * Spending not covered by any budget — including spending with no category
   * at all. Named rather than folded into a total, because a budget screen
   * that quietly omits the uncategorised is a budget screen that lies.
   */
  unbudgetedSpentPence: Pence,
});

export const BudgetQuery = z.object({ month: IsoMonth });

export const BudgetInput = z.strictObject({
  amountPence: Pence.min(0, "A budget cannot be negative"),
  period: BudgetPeriod.default("monthly"),
  /**
   * The month the new figure takes effect. **Earlier months keep the old one**
   * — that is the whole point of dating a budget, and it is why setting one
   * closes the previous row rather than overwriting it.
   */
  startsMonth: IsoMonth,
  endsMonth: IsoMonth.nullable().default(null),
  /**
   * Which accounts to measure this budget against. Empty (the default) means
   * every account — a household with one shared budget never has to think
   * about this field at all.
   *
   * Capped the same as `QueryFilters.accounts`, and for the same reason: D1's
   * 100-bound-parameter limit.
   */
  accountIds: z.array(Id).max(20).default([]),
});

/** Stopping a budget from a month on, without erasing what it was before. */
export const BudgetEnd = z.object({ fromMonth: IsoMonth });

/**
 * A suggested monthly figure per category, from spending history —
 * deterministic, not a model call (docs/08 §8.10: the model never does
 * arithmetic). See `core/budgets.ts` for how the figure is worked out.
 */
export const BudgetSuggestionLine = z.object({
  categoryId: Id,
  categoryName: z.string(),
  /** The median of the window's months, rounded up to the nearest £25. */
  suggestedAmountPence: Pence,
  /** Of the months in the window, how many actually had spend in this category. */
  monthsWithSpend: z.number().int(),
});

export const BudgetSuggestions = z.object({
  /** How many trailing months the figures rest on. */
  months: z.number().int(),
  lines: z.array(BudgetSuggestionLine),
  /**
   * The household's average income over the same window, so a suggestion can
   * be read against what there actually was to budget from. Null when there
   * is no income history in the window at all.
   */
  averageIncomePence: Pence.nullable(),
});

export const BudgetSuggestionQuery = z.object({
  month: IsoMonth,
  months: z.coerce.number().int().min(2).max(24).default(6),
});

/**
 * A budgeted category judged on a whole year rather than on whichever month
 * happens to be on screen — the mean of its spend across the window, not the
 * one month a household happens to be looking at. See `core/budgets.ts` for
 * why this is the mean where a suggestion is the median.
 */
export const BudgetYearlyLine = z.object({
  categoryId: Id,
  categoryName: z.string(),
  kind: CategoryKind,
  colour: z.string().nullable(),
  /** This month's allowance — the same figure the monthly view compares against. */
  monthlyAllowancePence: Pence,
  /** The window's true mean, zero-filled the same way a suggestion is. */
  averageSpentPence: Pence,
  /** Of the months in the window, how many actually had spend in this category. */
  monthsWithSpend: z.number().int(),
});

export const BudgetYearly = z.object({
  month: IsoMonth,
  /** How many trailing months the figures rest on. */
  months: z.number().int(),
  /** Every category budgeted this month, judged on the window instead. */
  lines: z.array(BudgetYearlyLine),
});

export const BudgetYearlyQuery = z.object({
  month: IsoMonth,
  months: z.coerce.number().int().min(2).max(24).default(12),
});

/**
 * Insights — deterministic, computed, no model (docs/05 §5.4).
 *
 * Detecting a subscription that rose in price, or a category that doubled, is
 * a computation over the ledger. It should be right every time, cost nothing,
 * and work when the model is down.
 */
export const Cadence = z.enum(["weekly", "fortnightly", "monthly", "quarterly", "annual"]);

export const RecurringPayment = z.object({
  key: z.string(),
  label: z.string(),
  cadence: Cadence,
  /** The typical amount as a positive magnitude. */
  typicalPence: Pence,
  occurrences: z.number().int(),
  firstSeen: IsoDate,
  lastSeen: IsoDate,
  /** Only ever the next date implied by the cadence, never a promise. */
  expectedNext: IsoDate.nullable(),
  category: z.string(),
  /** Set when the amount stepped up and stayed there. */
  priceRise: z
    .object({
      fromPence: Pence,
      toPence: Pence,
      changedOn: IsoDate,
      /** Basis points, so the client does not have to divide. */
      percentBp: z.number().int(),
    })
    .nullable(),
});

export const CategoryMover = z.object({
  category: z.string(),
  colour: z.string().nullable(),
  spentPence: Pence,
  previousPence: Pence,
  changePence: Pence,
  /** Null when the previous month was zero — a rise from nothing has no ratio. */
  percentBp: z.number().int().nullable(),
  txnCount: z.number().int(),
});

export const UnusualSpend = z.object({
  txnId: z.string(),
  date: IsoDate,
  description: z.string(),
  category: z.string(),
  amountPence: Pence,
  /** What normal looks like in that category, so the flag explains itself. */
  typicalPence: Pence,
  multiple: z.number(),
});

export const Insights = z.object({
  month: IsoMonth,
  /** How much history the figures rest on. A thin month says so. */
  monthsAnalysed: z.number().int(),
  recurring: z.array(RecurringPayment),
  priceRises: z.array(RecurringPayment),
  movers: z.array(CategoryMover),
  unusual: z.array(UnusualSpend),
});

export const InsightsQuery = z.object({ month: IsoMonth });

export type Measure = z.infer<typeof Measure>;
export type GroupBy = z.infer<typeof GroupBy>;
export type SortOrder = z.infer<typeof SortOrder>;
export type CompareTo = z.infer<typeof CompareTo>;
export type RelativePeriod = z.infer<typeof RelativePeriod>;
export type AbsolutePeriod = z.infer<typeof AbsolutePeriod>;
export type Period = z.infer<typeof Period>;
export type RangePeriod = z.infer<typeof RangePeriod>;
export type QueryFilters = z.infer<typeof QueryFilters>;
export type Query = z.infer<typeof Query>;
export type QueryInput = z.input<typeof Query>;
export type QueryRow = z.infer<typeof QueryRow>;
export type ResolvedPeriod = z.infer<typeof ResolvedPeriod>;
export type QueryTotal = z.infer<typeof QueryTotal>;
export type QueryResult = z.infer<typeof QueryResult>;
export type BudgetPeriod = z.infer<typeof BudgetPeriod>;
export type BudgetLine = z.infer<typeof BudgetLine>;
export type BudgetSummary = z.infer<typeof BudgetSummary>;
export type BudgetQuery = z.infer<typeof BudgetQuery>;
export type BudgetInput = z.infer<typeof BudgetInput>;
export type BudgetEnd = z.infer<typeof BudgetEnd>;
export type BudgetSuggestionLine = z.infer<typeof BudgetSuggestionLine>;
export type BudgetSuggestions = z.infer<typeof BudgetSuggestions>;
export type BudgetSuggestionQuery = z.infer<typeof BudgetSuggestionQuery>;
export type BudgetYearlyLine = z.infer<typeof BudgetYearlyLine>;
export type BudgetYearly = z.infer<typeof BudgetYearly>;
export type BudgetYearlyQuery = z.infer<typeof BudgetYearlyQuery>;
export type Cadence = z.infer<typeof Cadence>;
export type RecurringPayment = z.infer<typeof RecurringPayment>;
export type CategoryMover = z.infer<typeof CategoryMover>;
export type UnusualSpend = z.infer<typeof UnusualSpend>;
export type Insights = z.infer<typeof Insights>;
export type InsightsQuery = z.infer<typeof InsightsQuery>;
